import { useMutation, useQueryClient } from '@tanstack/react-query';

import { postHasuraGraphql } from '../../../../utils/hasuraGraphql';
import { useToast } from '../../../Toast/context/useToast.context';

import { setupOnlinePublicationQueryKeys } from './setupOnlinePublicationQueryKeys';

// Built as a single mutation containing one `update_..._by_pk` call per menu
// id. Codegen can't type a query whose shape changes per call, so this stays
// as a raw string handed to `postHasuraGraphql`.
// TODO(BUD-XXXXX): replace with `update_publicationNavigationMenuItems_many`.
const buildReorderMenuItemsMutation = (orderedMenuIds: string[]): string => `
  mutation reorderMenuItems {
    ${orderedMenuIds
      .map(
        (menuId, index) => `
      menu${index}: update_publicationNavigationMenuItems_by_pk(
        _set: { order: ${index + 1} }
        pk_columns: { id: "${menuId}" }
      ) {
        id
        order
      }`
      )
      .join('')}
  }
`;

export type UseOrganizeMenuItemsMessages = {
  successTitle: string;
  successBody?: string;
  errorTitle: string;
  errorBody?: string;
};

export type OrganizeMenuItemsParams = {
  orderedMenuIds: string[];
  publicationId: string;
};

export function useOrganizeMenuItems(messages: UseOrganizeMenuItemsMessages) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  return useMutation<Record<string, { id: string; order: number }>, Error, OrganizeMenuItemsParams>({
    mutationKey: [...setupOnlinePublicationQueryKeys.all, 'organizeMenuItems'],
    mutationFn: ({ orderedMenuIds }) =>
      postHasuraGraphql<Record<string, { id: string; order: number }>>(
        buildReorderMenuItemsMutation(orderedMenuIds),
        {}
      ),
    onSuccess: (_data, { publicationId }) => {
      void queryClient.invalidateQueries({
        queryKey: setupOnlinePublicationQueryKeys.navigationMenus({ publicationId })
      });
      toggleToast({
        title: messages.successTitle,
        body: messages.successBody,
        severity: 'success'
      });
    },
    onError: () =>
      toggleToast({
        title: messages.errorTitle,
        body: messages.errorBody,
        severity: 'error'
      })
  });
}
